import './pdfPolyfills.ts';

/**
 * Where pdf.js loads its worker from.
 *
 * Served from our own origin. Vite rewrites this to a hashed asset at build time,
 * taking the worker from the same `pdfjs-dist` react-pdf depends on, so worker and
 * API versions cannot drift apart.
 *
 * The `legacy/` build, not `build/`: a worker has its own global, so the
 * main-thread polyfill in `pdfPolyfills.ts` never reaches it. The modern worker
 * calls `Promise.withResolvers()` from the `PDFWorkerStream`/`ChunkedStream`
 * constructors, which run during document *loading* — i.e. after the worker
 * handshake has already succeeded, so pdf.js does not fall back to a fake
 * worker; the load just fails and the user gets the "failed to load" empty
 * state. The legacy build ships its own `withResolvers` polyfill, for 40KB more
 * than the modern one (1.41MB vs 1.37MB, ~3%) (DC-1069).
 *
 * This previously pointed at `//unpkg.com/pdfjs-dist@<version>/build/pdf.worker.min.mjs`
 * in five separate components — a public CDN, over a protocol-relative URL with no
 * integrity hash, executing with access to the document being rendered. The CSP
 * report-only soak surfaced it (DC-1060), and keeping it off-origin would have meant
 * allowing a third-party CDN in `script-src`.
 */
export const PDF_WORKER_SRC = new URL(
	'pdfjs-dist/legacy/build/pdf.worker.min.mjs',
	import.meta.url
).toString();

/**
 * Options every `<Document>` and `getDocument()` call passes.
 *
 * `isEvalSupported: false` stops pdf.js compiling PDF function objects and glyph
 * outlines with `new Function`, which the CSP report-only soak flagged as a
 * `script-src` violation from the worker (DC-1060). pdf.js falls back to its
 * interpreter; the cost is render speed on the minority of documents carrying
 * PostScript (type 4) functions, not correctness.
 *
 * A module-level constant, not an inline literal: react-pdf reloads the document
 * whenever the `options` identity changes.
 */
export const PDF_DOCUMENT_OPTIONS = { isEvalSupported: false };
